import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./providers/AuthProvider";
import { AuthPage } from "../features/auth/AuthPage";
import { CreateFamilyRoute } from "../features/family/CreateFamilyRoute";
import { JoinFamily } from "../features/family/JoinFamily";
import { Layout } from "../components/Layout";
import { Dashboard } from "../features/dashboard/Dashboard";
import { AddWishForm } from "../features/wish/AddWishForm";
import { WishlistPage } from "../features/wish/WishlistPage";
import { MyReservationsPage } from "../features/reservation/MyReservationsPage";
import { FamilyOverview } from "../features/family/FamilyOverview";
import { EventsPage } from "../features/event/EventsPage";
import { NotificationsPage } from "../features/notification/NotificationsPage";
import { useFamilyWebSocket } from "./hooks/useFamilyWebSocket";
import { NotificationProvider } from "./providers/NotificationProvider";
import { PwaUpdatePrompt } from "../pwa/PwaUpdatePrompt";
import { ProfilePage } from "../features/auth/ProfilePage";

function ProtectedRoute() {
  const { isAuthenticated, isLoading, user } = useAuth();
  useFamilyWebSocket();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F2F1EC]">
        <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-[#FF7A00] border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <NotificationProvider key={user?.id ?? "authenticated"}>
      <Layout />
    </NotificationProvider>
  );
}

function AddWishPage() {
  const { memberId } = useParams<{ memberId: string }>();
  if (!memberId) {
    return <Navigate to="/" replace />;
  }
  return <AddWishForm memberId={memberId} />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<AuthPage />} />
      <Route path="/join" element={<JoinFamily />} />
      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/bookings" element={<MyReservationsPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/family" element={<FamilyOverview />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/families/new" element={<CreateFamilyRoute />} />
        <Route path="/families/:id" element={<Navigate to="/family" replace />} />
        <Route path="/members/:memberId/wishes" element={<WishlistPage />} />
        <Route path="/members/:memberId/wishes/new" element={<AddWishPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <PwaUpdatePrompt />
      </AuthProvider>
    </BrowserRouter>
  );
}
